import { createSelector } from '@reduxjs/toolkit';
import type { Map as ImmutableMap } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import type { Profile } from 'app-components/flux/profile/profileTypes';

import type { DatasetsStore } from './datasetsTypes';
import { DATASETS_REDUCER_KEY, type Dataset, mkDataset } from './datasetsTypes';
import * as datasetsUtils from './datasetsUtils';

export const getDatasetsStore = (state: { [DATASETS_REDUCER_KEY]: DatasetsStore }) => state[DATASETS_REDUCER_KEY];

export const getResourcesById = createSelector([getDatasetsStore], (store) => store.resourcesById);

/**
 * @deprecated use getResourcesById
 */
export const getDatasetsById = createSelector([getDatasetsStore], (store) => store.resourcesById);
export const getLoadPending = createSelector([getDatasetsStore], (store) => store.loadPending);
export const getIsLoading = createSelector([getDatasetsStore], (store) => store.isLoading);
/**
 * @deprecated use getIsLoading
 */
export const isLoading = createSelector([getDatasetsStore], (store) => store.isLoading);
export const getError = createSelector([getDatasetsStore], (store) => store.error);

export const getLastSyncDate = createSelector([getDatasetsStore], (store) => store.lastSyncDate);

// Datasets sorted by name only
export const getDatasets = createSelector(getResourcesById, (datasetsById) =>
  !datasetsById
    ? null
    : datasetsById.sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true, sensitivity: 'base' }),
      ),
);

// Datasets sorted by most recently used - it's important for selecting last used dataset after login
export const getSortedDatasets = createSelector(getResourcesById, (datasetsById) =>
  datasetsById?.sort((a, b) => {
    if (a.lastSyncedAt && b.lastSyncedAt) {
      return b.lastSyncedAt.localeCompare(a.lastSyncedAt); // hooked up to sync session
    }
    return (b.lastAccessedAt ?? '').localeCompare(a.lastAccessedAt ?? ''); // QCS randomly sets it
  }),
);

// exclude non active optimistic records
export const getActiveSortedDatasets = createSelector(getSortedDatasets, (datasetsById) =>
  datasetsById?.filter((dataset) => dataset.id),
);

export const getEntitledDatasetsLoadPending = createSelector(
  getResourcesById,
  getLoadPending,
  (datasets, loadPending) =>
    loadPending || !datasets.filter((dataset) => dataset.id).every((dataset) => Boolean(dataset.entitlements)),
);

export const getEntitledDatasets = createSelector(getActiveSortedDatasets, (datasetsById) =>
  datasetsById?.filter((dataset) => datasetsUtils.hasActiveEntitlement(dataset)),
);

// Keeps optimistic records; shared spaces the user can't open aren't their finances.
export const getDatasetsExcludingUnavailableShares = createSelector(getResourcesById, (datasetsById) =>
  datasetsById?.filterNot(datasetsUtils.isUnavailableShare),
);

type datasetsMaps = ImmutableMap<string, Dataset>;

const profileFerry = (_state: unknown, profile: Profile | undefined) => profile;

export const makeNewDataset = createSelector(getResourcesById, profileFerry, (datasetsById, profile) => {
  let name: string;
  const namePrototype = (profile && profile.firstName && `${profile.firstName}${"'"}s Finances`) || 'Finances';
  let counter = 0;
  do {
    name = counter ? `${namePrototype} ${counter}` : namePrototype;
    counter += 1;
  } while (datasetsById && (datasetsById as datasetsMaps).find((dataset) => dataset.name === name));
  const zipcode = (profile && profile.primaryAddress && profile.primaryAddress.zipCode) || '00000';
  const clientId = uuidv4().toUpperCase();

  return mkDataset({
    clientId,
    name,
    zipcode, // need to remove it if QCS aggree to make it optional (Mint legacy parameter)
  });
});

export const mkQEMDataset = createSelector(getResourcesById, profileFerry, (datasetsById, profile) => {
  let name: string;
  const namePrototype = datasetsUtils.makeQEMDatasetNameFromProfile(profile);
  let counter = 0;
  do {
    name = counter ? `${namePrototype} ${counter}` : namePrototype;
    counter += 1;
  } while (datasetsById && (datasetsById as datasetsMaps).find((dataset) => dataset.name === name));
  const zipcode = (profile && profile.primaryAddress && profile.primaryAddress.zipCode) || '00000';
  const clientId = uuidv4().toUpperCase();

  return mkDataset({
    clientId,
    name,
    zipcode, // need to remove it if QCS agree to make it optional (Mint legacy parameter)
  });
});
